/**
 * ResourceRegistry - Centralized tracking and disposal of GPU/audio/video resources
 *
 * Tracks all resources that may contend for GPU during exports:
 * - Three.js: geometries, materials, textures, renderers
 * - WebCodecs: VideoEncoder, AudioEncoder, VideoFrame, AudioData
 * - Physics: CANNON.js bodies
 *
 * Provides auditing capabilities to verify clean state before/after exports.
 */

/**
 * Resource categories for disposal ordering and reporting
 * Disposal order: WEBCODECS_FRAME -> WEBCODECS_ENCODER -> GPU_TEXTURE -> GPU_MATERIAL -> GPU_GEOMETRY -> GPU_RENDERER -> PHYSICS_BODY
 */
export const RESOURCE_CATEGORIES = {
  GPU_GEOMETRY: 'gpu_geometry',
  GPU_MATERIAL: 'gpu_material',
  GPU_TEXTURE: 'gpu_texture',
  GPU_RENDERER: 'gpu_renderer',
  WEBCODECS_ENCODER: 'webcodecs_encoder',
  WEBCODECS_FRAME: 'webcodecs_frame',
  PHYSICS_BODY: 'physics_body',
  CANVAS: 'canvas',
  OTHER: 'other',
};

/**
 * Disposal order - resources disposed in this sequence
 * Critical: WebCodecs frames must close before encoders
 */
const DISPOSAL_ORDER = [
  RESOURCE_CATEGORIES.WEBCODECS_FRAME,
  RESOURCE_CATEGORIES.WEBCODECS_ENCODER,
  RESOURCE_CATEGORIES.GPU_TEXTURE,
  RESOURCE_CATEGORIES.GPU_MATERIAL,
  RESOURCE_CATEGORIES.GPU_GEOMETRY,
  RESOURCE_CATEGORIES.GPU_RENDERER,
  RESOURCE_CATEGORIES.PHYSICS_BODY,
  RESOURCE_CATEGORIES.CANVAS,
  RESOURCE_CATEGORIES.OTHER,
];

/**
 * Infer the dispose method for a resource based on its API
 */
function inferDispose(obj) {
  if (!obj) return null;

  // WebCodecs: close()
  if (typeof obj.close === 'function') {
    return () => obj.close();
  }

  // WebGPU: destroy()
  if (typeof obj.destroy === 'function') {
    return () => obj.destroy();
  }

  // Three.js: dispose()
  if (typeof obj.dispose === 'function') {
    return () => obj.dispose();
  }

  // Audio nodes: disconnect()
  if (typeof obj.disconnect === 'function') {
    return () => obj.disconnect();
  }

  return null;
}

/**
 * ResourceRegistry - tracks resources for a specific scope (visualizer instance, export session)
 */
export class ResourceRegistry {
  /**
   * @param {string} label - Identifier for this registry (e.g., 'spheres3d-export', 'preview-visualizer')
   */
  constructor(label = 'resources') {
    this.label = label;
    this.items = new Map(); // id -> entry
    this._nextId = 1;
    this._disposed = false;
  }

  /**
   * Track a resource for later disposal
   * @param {Object} obj - The resource to track
   * @param {Object} options - Tracking options
   * @param {string} options.name - Human-readable name for debugging
   * @param {string} options.type - Resource type (e.g., 'THREE.SphereGeometry')
   * @param {string} options.category - RESOURCE_CATEGORIES value (auto-detected if not provided)
   * @param {Function} options.dispose - Custom dispose function (auto-detected if not provided)
   * @returns {Object} The tracked resource (for chaining)
   */
  track(obj, { name, type, category, dispose } = {}) {
    if (!obj) return obj;
    if (this._disposed) {
      console.warn(`[${this.label}] Attempting to track resource on disposed registry`);
      return obj;
    }

    const id = this._nextId++;
    const resolvedType = type || obj?.constructor?.name || 'resource';
    const resolvedCategory = category || this._inferCategory(resolvedType);

    const entry = {
      id,
      name: name || `${resolvedType}#${id}`,
      type: resolvedType,
      category: resolvedCategory,
      obj,
      dispose: dispose || inferDispose(obj),
      createdAt: performance.now(),
      disposed: false,
    };

    // Capture stack in development for leak debugging
    if (import.meta.env?.DEV) {
      entry.stack = new Error().stack;
    }

    this.items.set(id, entry);

    // Tag object for easier debugging
    try {
      obj.__resId = id;
      obj.__resRegistry = this.label;
    } catch {
      // Some objects are frozen or have read-only properties
    }

    return obj;
  }

  /**
   * Untrack a resource without disposing it
   * @param {Object|number} objOrId - The resource or its ID
   */
  untrack(objOrId) {
    if (objOrId === null || objOrId === undefined) return;

    // If given an ID directly
    if (typeof objOrId === 'number') {
      this.items.delete(objOrId);
      return;
    }

    // If given an object, find it by ID tag or reference
    const id = objOrId.__resId;
    if (id !== undefined && this.items.has(id)) {
      this.items.delete(id);
      return;
    }

    // Fallback: search by reference
    for (const [entryId, entry] of this.items) {
      if (entry.obj === objOrId) {
        this.items.delete(entryId);
        return;
      }
    }
  }

  /**
   * Dispose a single resource and untrack it
   * @param {Object|number} objOrId - The resource or its ID
   */
  dispose(objOrId) {
    if (objOrId === null || objOrId === undefined) return;

    // Find the entry
    let entry = null;
    let entryId = null;

    if (typeof objOrId === 'number') {
      entry = this.items.get(objOrId);
      entryId = objOrId;
    } else {
      const id = objOrId.__resId;
      if (id !== undefined && this.items.has(id)) {
        entry = this.items.get(id);
        entryId = id;
      } else {
        // Search by reference
        for (const [id, e] of this.items) {
          if (e.obj === objOrId) {
            entry = e;
            entryId = id;
            break;
          }
        }
      }
    }

    if (entry) {
      this._disposeEntry(entry);
      this.items.delete(entryId);
    } else if (typeof objOrId === 'object') {
      // Not tracked - best-effort dispose anyway
      const disposeFunc = inferDispose(objOrId);
      try {
        disposeFunc?.();
      } catch (err) {
        console.warn(`[${this.label}] Best-effort dispose failed:`, err);
      }
    }
  }

  /**
   * Dispose all tracked resources in the correct order
   * @param {Object} options
   * @param {boolean} options.log - Whether to log disposal count
   */
  disposeAll({ log = true } = {}) {
    if (this._disposed) return;

    const entries = [...this.items.values()];
    const byCategory = new Map();

    // Group by category
    for (const entry of entries) {
      const cat = entry.category || RESOURCE_CATEGORIES.OTHER;
      if (!byCategory.has(cat)) {
        byCategory.set(cat, []);
      }
      byCategory.get(cat).push(entry);
    }

    // Dispose in order
    let disposedCount = 0;
    for (const category of DISPOSAL_ORDER) {
      const categoryEntries = byCategory.get(category) || [];
      for (const entry of categoryEntries) {
        this._disposeEntry(entry);
        disposedCount++;
      }
    }

    if (log && disposedCount > 0) {
      console.log(`[${this.label}] Disposed ${disposedCount} tracked resources`);
    }

    this.items.clear();
    this._disposed = true;
  }

  /**
   * Get a report of tracked resources
   * @param {Object} options
   * @param {boolean} options.groupByType - Whether to group by type
   * @returns {Object} Report with counts and details
   */
  report({ groupByType = true } = {}) {
    const entries = [...this.items.values()];

    if (!groupByType) {
      return { total: entries.length, entries };
    }

    const byType = {};
    const byCategory = {};

    for (const entry of entries) {
      // By type
      byType[entry.type] = (byType[entry.type] || 0) + 1;

      // By category
      const cat = entry.category || RESOURCE_CATEGORIES.OTHER;
      byCategory[cat] = (byCategory[cat] || 0) + 1;
    }

    return {
      total: entries.length,
      byType,
      byCategory,
    };
  }

  /**
   * Dump the most recent resources to console for debugging
   * @param {Object} options
   * @param {number} options.limit - Max resources to show
   */
  dumpTop({ limit = 30 } = {}) {
    const entries = [...this.items.values()]
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limit)
      .map((e) => ({
        id: e.id,
        name: e.name,
        type: e.type,
        category: e.category,
        age: `${Math.round(performance.now() - e.createdAt)}ms`,
      }));

    console.log(`[${this.label}] Top ${entries.length} resources:`);
    console.table(entries);
    console.log(`[${this.label}] Full report:`, this.report());
  }

  /**
   * Verify all resources have been disposed
   * @returns {boolean} True if no resources remain
   */
  verify() {
    if (this.items.size > 0) {
      console.warn(
        `[${this.label}] LEAK: ${this.items.size} resources not disposed`,
        this.report()
      );
      return false;
    }
    return true;
  }

  /**
   * Check if the registry has been disposed
   */
  get disposed() {
    return this._disposed;
  }

  /**
   * Get the count of tracked resources
   */
  get size() {
    return this.items.size;
  }

  /**
   * Internal: dispose a single entry
   */
  _disposeEntry(entry) {
    if (entry.disposed) return;

    try {
      entry.dispose?.();
    } catch (err) {
      console.warn(`[${this.label}] Dispose failed for ${entry.name}:`, err);
    } finally {
      entry.disposed = true;
    }
  }

  /**
   * Internal: infer category from type name
   */
  _inferCategory(typeName) {
    const name = typeName.toLowerCase();

    if (name.includes('videoframe') || name.includes('audiodata')) {
      return RESOURCE_CATEGORIES.WEBCODECS_FRAME;
    }
    if (name.includes('encoder')) {
      return RESOURCE_CATEGORIES.WEBCODECS_ENCODER;
    }
    if (name.includes('texture') || name.includes('envmap') || name.includes('canvastexture')) {
      return RESOURCE_CATEGORIES.GPU_TEXTURE;
    }
    if (name.includes('material')) {
      return RESOURCE_CATEGORIES.GPU_MATERIAL;
    }
    if (name.includes('geometry') || name.includes('buffergeometry')) {
      return RESOURCE_CATEGORIES.GPU_GEOMETRY;
    }
    if (name.includes('renderer')) {
      return RESOURCE_CATEGORIES.GPU_RENDERER;
    }
    if (name.includes('body') || name.includes('cannon')) {
      return RESOURCE_CATEGORIES.PHYSICS_BODY;
    }
    if (name.includes('canvas')) {
      return RESOURCE_CATEGORIES.CANVAS;
    }

    return RESOURCE_CATEGORIES.OTHER;
  }
}

/**
 * ResourceAuditor - Static utilities for auditing resources across registries
 */
export class ResourceAuditor {
  static registries = new Set();

  /**
   * Register a registry for global auditing
   */
  static registerRegistry(registry) {
    this.registries.add(registry);
  }

  /**
   * Unregister a registry
   */
  static unregisterRegistry(registry) {
    this.registries.delete(registry);
  }

  /**
   * Audit all registries before export
   * @returns {Object} Audit report with issues and warnings
   */
  static auditPreExport() {
    const report = {
      timestamp: Date.now(),
      registriesCount: this.registries.size,
      totalResources: 0,
      issues: [],
      warnings: [],
    };

    for (const registry of this.registries) {
      if (registry.disposed) continue;

      const regReport = registry.report();
      report.totalResources += regReport.total;

      // Check for old resources (potential leaks)
      for (const entry of registry.items.values()) {
        const age = performance.now() - entry.createdAt;
        if (age > 60000) {
          // Older than 60s
          report.warnings.push(
            `Registry "${registry.label}": ${entry.name} is ${Math.round(age / 1000)}s old (possible leak)`
          );
        }
      }

      // Check for unclosed encoders
      const encoderCount = regReport.byCategory?.[RESOURCE_CATEGORIES.WEBCODECS_ENCODER] || 0;
      if (encoderCount > 0) {
        report.issues.push(
          `Registry "${registry.label}" has ${encoderCount} unclosed encoder(s)`
        );
      }

      // Check for unclosed frames
      const frameCount = regReport.byCategory?.[RESOURCE_CATEGORIES.WEBCODECS_FRAME] || 0;
      if (frameCount > 0) {
        report.issues.push(
          `Registry "${registry.label}" has ${frameCount} unclosed frame(s)`
        );
      }
    }

    report.clean = report.issues.length === 0;
    return report;
  }

  /**
   * Audit all registries after export to verify cleanup
   * @returns {Object} Audit report
   */
  static auditPostExport() {
    const report = this.auditPreExport();

    // After export, all export-related registries should be empty
    for (const registry of this.registries) {
      if (registry.disposed) continue;

      if (registry.items.size > 0 && registry.label.includes('export')) {
        report.issues.push(
          `Registry "${registry.label}" still has ${registry.items.size} resources after export`
        );
      }
    }

    report.clean = report.issues.length === 0;
    return report;
  }

  /**
   * Dump all registries for debugging
   */
  static dumpAll() {
    console.log(`[ResourceAuditor] ${this.registries.size} registries:`);
    for (const registry of this.registries) {
      if (!registry.disposed) {
        registry.dumpTop({ limit: 10 });
      }
    }
  }
}
